import { z } from 'zod';
import { MoneySchema } from '../common';
import {
  AccessReason,
  BookingEscrowState,
  ContractFundingIntentStatus,
  ContractFundingSource,
  CtaMode,
  EntitlementStatus,
  PaymentStatus,
  PayoutState,
  ReplayPurchaseStatus,
  SettlementProfileType,
} from './payment.enums';
import { EventInviteStatus, EventReplayAccess } from '../event';

export const InitiatePaymentRequestSchema = z.object({
  eventId: z.uuid(),
  amount: z.number().min(0),
});

export const InitiatePaymentResponseSchema = z.object({
  authorizationUrl: z.url(),
  reference: z.string(),
  accessCode: z.string().optional(),
});

export const CreatePurchaseResponseSchema = z.object({
  reference: z.string(),
  checkoutUrl: z.url().nullable(),
  status: z.enum(PaymentStatus),
  expiresAt: z.iso.datetime(),
});
export const PurchaseStatusResponseSchema = z.object({
  reference: z.string(),
  status: z.enum(PaymentStatus),
  eventId: z.uuid(),
  amount: MoneySchema,
  paidAt: z.iso.datetime().nullable(),
});

export const AccessCheckRequestSchema = z.object({
  /**
   * @deprecated Ignored: the check only ever evaluates the signed-in caller,
   * so it can't be used to probe a guest list.
   */
  email: z.email().optional(),
});

const PaystackMetadataSchema = z
  .object({
    intent_id: z.uuid(),
    user_id: z.uuid(),
    event_id: z.uuid(),
  })
  .loose();

const PaystackAuthorizationSchema = z
  .object({
    authorization_code: z.string(),
    bin: z.string().nullish(),
    last4: z.string().nullish(),
    exp_month: z.string().nullish(),
    exp_year: z.string().nullish(),
    channel: z.string().nullish(),
    card_type: z.string().nullish(),
    bank: z.string().nullish(),
    country_code: z.string().nullish(),
    brand: z.string().nullish(),
    reusable: z.boolean().nullish(),
    signature: z.string().nullish(),
    account_name: z.string().nullish(),
    receiver_bank_account_number: z.string().nullish(),
    receiver_bank: z.string().nullish(),
  })
  .loose();

const PaystackCustomerSchema = z
  .object({
    id: z.number(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    email: z.email(),
    customer_code: z.string(),
    phone: z.string().nullish(),
    metadata: z.unknown().nullish(),
    risk_action: z.string().nullish(),
    international_format_phone: z.string().nullish(),
  })
  .loose();

const PaystackSourceSchema = z
  .object({
    type: z.string().nullish(),
    source: z.string().nullish(),
    entry_point: z.string().nullish(),
    identifier: z.string().nullish(),
  })
  .loose();

export const PaystackWebhookSchema = z
  .object({
    event: z.string(),
    data: z
      .object({
        id: z.number(),
        domain: z.string().optional(),
        status: z.string(),
        reference: z.string(),
        amount: z.number(),
        message: z.string().nullish(),
        gateway_response: z.string().nullish(),
        paid_at: z.string().nullish(),
        paidAt: z.string().nullish(),
        created_at: z.string().nullish(),
        channel: z.string().nullish(),
        currency: z.string(),
        ip_address: z.string().nullish(),
        fees: z.number().nullish(),
        fees_breakdown: z.unknown().nullish(),
        fees_split: z.unknown().nullish(),
        log: z.unknown().nullish(),
        order_id: z.string().nullish(),
        requested_amount: z.number().nullish(),
        pos_transaction_data: z.unknown().nullish(),
        plan: z.object({}).loose().optional(),
        subaccount: z.object({}).loose().optional(),
        split: z.object({}).loose().optional(),
        metadata: PaystackMetadataSchema,
        authorization: PaystackAuthorizationSchema.optional(),
        customer: PaystackCustomerSchema.optional(),
        source: PaystackSourceSchema.optional(),
      })
      .loose(),
  })
  .loose();

export const AccessCheckResponseSchema = z.object({
  qualified: z.boolean(),
  reason: z.enum(AccessReason),
});

export const PricingQuoteResponseSchema = z.object({
  reference: z.null(),
  subtotal: MoneySchema,
  platformFee: MoneySchema,
  total: MoneySchema,
  strategyId: z.string(),
  expiresInSec: z.number().int().positive(),
});

export const TrailerSchema = z.object({
  kind: z.literal('TRAILER'),
  trailerUrl: z.url(),
});

export const SlateSchema = z.object({
  kind: z.literal('SLATE'),
  slate: z.object({
    title: z.string(),
    hostDisplayName: z.string().nullable(),
    scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  }),
});

export const AwaitingIngestSchema = z.discriminatedUnion('kind', [TrailerSchema, SlateSchema]);

/** The viewer's invite to a private event, when they have one. */
export const ViewerEventInviteSchema = z.object({
  invitedAt: z.iso.datetime(),
  /** DECLINED invites still grant access if the viewer changes their mind. */
  status: z.enum(EventInviteStatus),
});

export const ViewerEventDetailResponseSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  category: z.string(),
  timezone: z.string(),
  scheduledStart: z.iso.datetime(),
  scheduledEnd: z.iso.datetime(),
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  scheduledEndLocal: z.iso.datetime({ local: true, offset: false }),
  state: z.string(),
  accessModel: z.string(),
  visibility: z.string(),
  ticketPrice: MoneySchema.nullable(),
  replayAccess: z.enum(EventReplayAccess),
  replayAccessFee: MoneySchema.nullable(),
  isFeatured: z.boolean(),
  hostDisplayName: z.string().nullable(),
  hostBio: z.string().nullable(),
  previewClipUrl: z.string().nullable(),
  remainingTickets: z.number().int().nullable(),
  liveStartedAt: z.iso.datetime().nullable(),
  liveEndedAt: z.iso.datetime().nullable(),
  replayAvailableUntil: z.iso.datetime().nullable(),
  ingestSubstate: z.enum(['AWAITING_INGEST', 'STREAMING']).nullable(),
  awaitingIngestContent: AwaitingIngestSchema.nullable(),
  /** Only guests on the host's list can join. */
  inviteOnly: z.boolean(),
  viewer: z.object({
    entitlement: z.enum(EntitlementStatus),
    purchaseCta: z.enum(CtaMode),
    activeIntentReference: z.string().optional(),
    /** @deprecated Read `purchaseCta === 'NOT_INVITED'` instead. */
    requiresAccessCheck: z.boolean().optional(),
    invite: ViewerEventInviteSchema.nullable(),
  }),
});

export const ReceiptUrlResponseSchema = z.object({
  url: z.url(),
  expiresAt: z.iso.datetime(),
  contentType: z.string().min(1),
});

export const ReplayPurchasePaymentSchema = z.object({
  reference: z.string(),
  amount: MoneySchema,
  checkoutUrl: z.string().optional(),
});

export const ReplayPurchaseRowSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  status: z.enum(ReplayPurchaseStatus),
  source: z.enum(ReplayPurchaseStatus),
  amount: MoneySchema,
  paymentExpiresAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  paidAt: z.iso.datetime().nullable(),
});

export const ReplayPurchaseResponseSchema = z.object({
  replayPurchase: ReplayPurchaseRowSchema,
  payment: ReplayPurchasePaymentSchema.nullable(),
});

export const UpdateSettlementBodySchema = z.object({
  bankCode: z
    .string()
    .trim()
    .regex(/^\d{3,10}$/, 'bankCode must be a 3–10 digit numeric string'),
  accountNumber: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'accountNumber must be a 10-digit NUBAN'),
});

export const SettlementAccountViewSchema = z.object({
  bankName: z.string().nullable(),
  bankCode: z.string().nullable(),
  accountNumberMasked: z.string().nullable(),
  accountName: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

/**
 * `POST /host/payouts/withdraw` and `POST /vendor/payouts/withdraw`
 * take no body — step-up auth is proven via the `X-Reauth-Token`
 * header minted by `POST /auth/confirm-pin` (action `WITHDRAW`).
 * Kept as an empty object schema so existing DTO wiring continues to
 * bind cleanly.
 */
export const RequestWithdrawalBodySchema = z.object({});

/**
 * `POST /marketplace/bookings/:id/fund-escrow`. The client names the funding
 * route; the amount is read from the booking escrow, never from the request
 * (§3.5: amounts are derived server side). Replay is keyed on the
 * `Idempotency-Key` header, and step-up auth on `X-Reauth-Token`
 * (`FUND_ESCROW`).
 */
export const FundEscrowBodySchema = z.object({
  source: z.enum(ContractFundingSource),
});

/**
 * A gateway contract funding intent (§8.2 route 1). `amount` is what reaches
 * contract escrow; `providerFee` is charged to the Host on top at checkout
 * under the customer-bears decision (F-03), so the gateway settles `amount`.
 *
 * `checkoutUrl` is null until the provider session exists, and after a
 * terminal transition.
 */
export const ContractFundingIntentResponseSchema = z.object({
  intentId: z.uuid(),
  bookingId: z.uuid(),
  reference: z.string(),
  status: z.enum(ContractFundingIntentStatus),
  amount: MoneySchema,
  providerFee: MoneySchema,
  checkoutUrl: z.url().nullable(),
  expiresAt: z.iso.datetime(),
  fundedAt: z.iso.datetime().nullable(),
});

/**
 * Result of funding an escrow. `BALANCE` settles inside the request, so the
 * escrow comes back `FUNDED` with no `fundingIntent`. `GATEWAY` leaves the
 * escrow `AWAITING_FUNDING` and returns the intent carrying the checkout URL
 * the Host must complete — §8.2: a payment must match the intent on amount,
 * reference, currency and expiry before the escrow is credited.
 */
export const FundEscrowResponseSchema = z.object({
  bookingId: z.uuid(),
  source: z.enum(ContractFundingSource),
  escrow: z.object({
    state: z.enum(BookingEscrowState),
    amount: MoneySchema,
  }),
  /** Null for `BALANCE`. Populated for `GATEWAY`. */
  fundingIntent: ContractFundingIntentResponseSchema.nullable(),
});

export const PandascrowEnvelopeSchema = <T extends z.ZodTypeAny>(data: T) =>
  z.object({
    status: z.union([z.boolean(), z.string()]).optional(),
    message: z.string().optional(),
    data: data,
  });

/** Loose envelope used for the pre-flight status check (§request). */
export const OuterEnvelopeSchema = z.object({
  status: z.union([z.boolean(), z.string()]).optional(),
  message: z.string().optional(),
  data: z.unknown().optional(),
});

// Amount fields on the wire — Pandascrow docs say "lowest denomination
// as whole numbers", so all money is minor units and could arrive as
// number or numeric string. Coerce to bigint on parse.
export const MinorAmountSchema = z
  .union([z.number(), z.string()])
  .transform((v) => BigInt(v as string | number));

const CurrencyStr = z.string();

export const OnetimeEscrowDataSchema = z.object({
  id: z.string().min(1),
  status: z.string().min(1),
  amount: MinorAmountSchema.optional(),
  currency: CurrencyStr.optional(),
  funding: z
    .object({
      account_number: z.string(),
      account_name: z.string(),
      bank_name: z.string(),
      expires_at: z.string().optional(),
    })
    .partial()
    .optional(),
});

export const WalletFundDataSchema = z.object({
  id: z.string().optional(),
  transaction_id: z.string().optional(),
  reference: z.string().optional(),
  status: z.string().min(1),
});

export const MarkCompleteDataSchema = z.object({
  id: z.string().optional(),
  transfer_id: z.string().optional(),
  status: z.string().min(1),
  requires_otp: z.boolean().optional(),
});

export const RefundFlagDataSchema = z.object({
  id: z.string().optional(),
  refund_id: z.string().optional(),
  flag_id: z.string().optional(),
  status: z.string().min(1),
});

export const EscrowStatusDataSchema = z.object({
  id: z.string().min(1),
  status: z.string().min(1),
  amount: MinorAmountSchema.optional(),
  currency: CurrencyStr.optional(),
  updated_at: z.string().optional(),
});

export const PayoutViewSchema = z.object({
  id: z.string(),
  reference: z.string(),
  state: z.enum(PayoutState),
  amount: MoneySchema,
  bankName: z.string().nullable(),
  accountNumberMasked: z.string(),
  accountName: z.string(),
  requestedAt: z.string(),
  settledAt: z.string().nullable(),
  failedAt: z.string().nullable(),
  failureReason: z.string().nullable(),
});

/**
 * `GET /wallet` (§5.1 `WALLET[account]`).
 *
 * `withdrawable` is deliberately not the whole balance. Only earned value
 * may leave by bank payout — A-10 / I-27 keep refund and reversal credit
 * on-platform, spendable but not withdrawable — and `restricted` is that
 * remainder. Surface both: a client that shows one figure and is then
 * refused a withdrawal has no way to explain why.
 */
export const WalletSummarySchema = z.object({
  balance: MoneySchema,
  withdrawable: MoneySchema,
  restricted: MoneySchema,
});

/**
 * `POST /wallet/move-earnings` — §6.1's one-directional movement of released
 * earnings into the account Wallet. There is no reverse route: §6.2
 * prohibits moving Wallet value back into an earnings position.
 */
export const MoveEarningsToWalletBodySchema = z.object({
  profileType: z.enum(SettlementProfileType),
  amountMinor: MinorAmountSchema.refine((v) => v > 0n, 'amountMinor must be positive'),
});

export const MoveEarningsToWalletResponseSchema = z.object({
  moved: MoneySchema,
});

/**
 * `POST /wallet/withdraw`. Takes no amount — the engine drains the
 * withdrawable slice reported by `GET /wallet` — and no PIN: step-up auth is
 * the `X-Reauth-Token` header minted by `POST /auth/confirm-pin` (action
 * `WITHDRAW`), as for the two profile withdraw routes.
 */
export const WalletWithdrawBodySchema = z.object({
  profileType: z.enum(SettlementProfileType),
});
